import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Button,
  Badge,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Textarea,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import {
  adminPaymentsApi,
  companiesApi,
  type PaymentRequest,
  type PaymentsDashboard,
} from "@/services/api";
import {
  CheckCircle,
  Loader2,
  XCircle,
  Clock,
  MoreVertical,
  Eye,
  Send,
  RefreshCw,
  Settings,
} from "lucide-react";
import { formatDate } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────

interface Company {
  id: string;
  razaoSocial?: string;
  nomeFantasia?: string;
  name?: string;
}

// ─── Status Config ────────────────────────────────────────

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    variant: "default" | "secondary" | "success" | "warning" | "destructive";
    icon: React.ComponentType<{ className?: string }>;
  }
> = {
  DRAFT: { label: "Rascunho", variant: "secondary", icon: Clock },
  PENDING_APPROVAL: { label: "Aguardando Aprovação", variant: "warning", icon: Clock },
  APPROVED: { label: "Aprovado", variant: "success", icon: CheckCircle },
  PROCESSING: { label: "Processando", variant: "warning", icon: Loader2 },
  AWAITING_DEPOSIT: { label: "Aguardando Depósito", variant: "warning", icon: Clock },
  DEPOSITED: { label: "Depositado", variant: "default", icon: CheckCircle },
  COMPLETED: { label: "Concluído", variant: "success", icon: CheckCircle },
  FAILED: { label: "Falhou", variant: "destructive", icon: XCircle },
  CANCELLED: { label: "Cancelado", variant: "secondary", icon: XCircle },
  REJECTED: { label: "Rejeitado", variant: "destructive", icon: XCircle },
};

const RAIL_LABELS: Record<string, string> = {
  wire: "Wire Transfer",
  sepa: "SEPA",
  spei: "SPEI",
};

// ─── Helpers ──────────────────────────────────────────────

function formatCurrencyValue(value: string | number | null | undefined, currency = "USD"): string {
  if (!value) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";

  const currencyMap: Record<string, string> = { USD: "en-US", EUR: "de-DE", MXN: "es-MX" };
  const locale = currencyMap[currency] || "en-US";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(num);
}

function formatUsdcValue(value: string | number | null | undefined): string {
  if (!value) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  return `${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 })} USDC`;
}

// ─── Main Component ───────────────────────────────────────

export function AdminPaymentsPage() {
  const navigate = useNavigate();

  // Dashboard stats
  const [dashboard, setDashboard] = useState<PaymentsDashboard | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    try {
      const data = await adminPaymentsApi.getDashboard();
      setDashboard(data);
    } catch (error) {
      console.error("Erro ao carregar dashboard:", error);
    }
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Pagamentos Internacionais"
        description="Aprovações e configurações de pagamentos"
        stats={
          dashboard
            ? [
                { value: dashboard.pending, label: "Pendentes" },
                { value: dashboard.processing, label: "Processando" },
                { value: dashboard.completed, label: "Concluídos" },
                { value: dashboard.failed, label: "Falhas" },
              ]
            : []
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => navigate("/admin/payments/settings")}
            >
              <Settings className="w-4 h-4 mr-2" />
              Configurações
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={loadDashboard}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>
        }
      />

      <ApprovalsTab onRefreshDashboard={loadDashboard} />
    </div>
  );
}

// ─── Approvals Tab ────────────────────────────────────────

function ApprovalsTab({ onRefreshDashboard }: { onRefreshDashboard: () => void }) {
  const navigate = useNavigate();

  // Data
  const [payments, setPayments] = useState<PaymentRequest[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");

  // Approve dialog
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentRequest | null>(null);
  const [approveNotes, setApproveNotes] = useState("");
  const [approving, setApproving] = useState(false);

  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadPayments();
  }, [currentPage, statusFilter, currencyFilter, companyFilter]);

  async function loadCompanies() {
    try {
      const data = await companiesApi.list();
      setCompanies(data);
    } catch (error) {
      console.error("Erro ao carregar empresas:", error);
    }
  }

  async function loadPayments() {
    setLoading(true);
    try {
      const data = await adminPaymentsApi.listPayments({
        page: currentPage,
        limit: pageSize,
        status: statusFilter !== "all" ? statusFilter : undefined,
        currency: currencyFilter !== "all" ? currencyFilter : undefined,
        companyId: companyFilter !== "all" ? companyFilter : undefined,
      });
      setPayments(data.items);
      setTotal(data.total);
    } catch (error) {
      console.error("Erro ao carregar pagamentos:", error);
    } finally {
      setLoading(false);
    }
  }

  function getStatusBadge(status: string) {
    const config = STATUS_CONFIG[status] || { label: status, variant: "secondary" as const, icon: Clock };
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
        <Icon className={`w-3 h-3 ${status === "PROCESSING" ? "animate-spin" : ""}`} />
        {config.label}
      </Badge>
    );
  }

  function getCompanyName(payment: PaymentRequest): string {
    // The payment might have companyName from the admin endpoint
    const p = payment as PaymentRequest & { companyName?: string; companyId?: string };
    if (p.companyName) return p.companyName;
    if (p.companyId) {
      const company = companies.find((c) => c.id === p.companyId);
      if (company) return company.razaoSocial || company.nomeFantasia || company.name || "-";
    }
    return "-";
  }

  function openApproveDialog(payment: PaymentRequest) {
    setSelectedPayment(payment);
    setApproveNotes("");
    setApproveDialogOpen(true);
  }

  function openRejectDialog(payment: PaymentRequest) {
    setSelectedPayment(payment);
    setRejectReason("");
    setRejectDialogOpen(true);
  }

  async function handleApprove() {
    if (!selectedPayment) return;
    setApproving(true);
    try {
      await adminPaymentsApi.approvePayment(selectedPayment.id, {
        adminNotes: approveNotes || undefined,
      });
      setApproveDialogOpen(false);
      loadPayments();
      onRefreshDashboard();
    } catch (error) {
      console.error("Erro ao aprovar pagamento:", error);
    } finally {
      setApproving(false);
    }
  }

  async function handleReject() {
    if (!selectedPayment || !rejectReason.trim()) return;
    setRejecting(true);
    try {
      await adminPaymentsApi.rejectPayment(selectedPayment.id, {
        reason: rejectReason,
      });
      setRejectDialogOpen(false);
      loadPayments();
      onRefreshDashboard();
    } catch (error) {
      console.error("Erro ao rejeitar pagamento:", error);
    } finally {
      setRejecting(false);
    }
  }

  const totalPages = Math.ceil(total / pageSize);
  const hasActiveFilters = statusFilter !== "all" || currencyFilter !== "all" || companyFilter !== "all";

  function clearFilters() {
    setStatusFilter("all");
    setCurrencyFilter("all");
    setCompanyFilter("all");
    setCurrentPage(1);
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v);
            setCurrentPage(1);
          }}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="PENDING_APPROVAL">Aguardando Aprovação</SelectItem>
            <SelectItem value="APPROVED">Aprovado</SelectItem>
            <SelectItem value="PROCESSING">Processando</SelectItem>
            <SelectItem value="AWAITING_DEPOSIT">Aguardando Depósito</SelectItem>
            <SelectItem value="COMPLETED">Concluído</SelectItem>
            <SelectItem value="FAILED">Falhou</SelectItem>
            <SelectItem value="REJECTED">Rejeitado</SelectItem>
            <SelectItem value="CANCELLED">Cancelado</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={currencyFilter}
          onValueChange={(v) => {
            setCurrencyFilter(v);
            setCurrentPage(1);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Moeda" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas moedas</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="EUR">EUR</SelectItem>
            <SelectItem value="MXN">MXN</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={companyFilter}
          onValueChange={(v) => {
            setCompanyFilter(v);
            setCurrentPage(1);
          }}
        >
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Empresa" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as empresas</SelectItem>
            {companies.map((company) => (
              <SelectItem key={company.id} value={company.id}>
                {company.razaoSocial || company.nomeFantasia || company.name || company.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Limpar filtros
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : payments.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Send className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">
                {hasActiveFilters ? "Nenhum pagamento encontrado" : "Nenhum pagamento registrado"}
              </p>
              <p className="text-sm mt-1">
                {hasActiveFilters
                  ? "Ajuste os filtros para encontrar pagamentos."
                  : "Os pagamentos solicitados pelas empresas aparecerão aqui."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto overflow-y-visible">
              <table className="w-full">
                <thead className="border-b border-border bg-muted/30">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">Código</th>
                    <th className="p-4 font-medium">Empresa</th>
                    <th className="p-4 font-medium">Moeda</th>
                    <th className="p-4 font-medium">Valor</th>
                    <th className="p-4 font-medium">Taxa Serviço</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium">Data</th>
                    <th className="p-4 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr
                      key={payment.id}
                      className="border-b border-border last:border-0 hover:bg-muted/20 cursor-pointer"
                      onClick={() => navigate(`/admin/payments/${payment.id}`)}
                    >
                      <td className="p-4">
                        <span className="font-medium font-mono text-sm">
                          {payment.paymentCode}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="font-medium">{getCompanyName(payment)}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span className="font-medium">{payment.currency}</span>
                          <span className="text-xs text-muted-foreground">
                            {RAIL_LABELS[payment.paymentRail] || payment.paymentRail}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="font-semibold">
                          {formatCurrencyValue(payment.amount, payment.currency)}
                        </span>
                        {payment.amountUsdc && (
                          <p className="text-xs text-muted-foreground">
                            {formatUsdcValue(payment.amountUsdc)}
                          </p>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-sm">
                          {payment.serviceFeeAmount
                            ? formatCurrencyValue(payment.serviceFeeAmount, "USD")
                            : "-"}
                        </span>
                        {payment.serviceFeeType && (
                          <p className="text-xs text-muted-foreground">
                            {payment.serviceFeeType === "FIXED"
                              ? "Fixo"
                              : `${(parseFloat(payment.serviceFeeRate || "0") * 100).toFixed(2)}%`}
                          </p>
                        )}
                      </td>
                      <td className="p-4">{getStatusBadge(payment.status)}</td>
                      <td className="p-4">
                        <span className="text-sm text-muted-foreground">
                          {formatDate(payment.createdAt)}
                        </span>
                      </td>
                      <td className="p-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/admin/payments/${payment.id}`);
                              }}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Ver Detalhes
                            </DropdownMenuItem>
                            {payment.status === "PENDING_APPROVAL" && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openApproveDialog(payment);
                                  }}
                                  className="text-green-600"
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Aprovar
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openRejectDialog(payment);
                                  }}
                                  className="text-red-600"
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Rejeitar
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {total} pagamento{total !== 1 ? "s" : ""} encontrado{total !== 1 ? "s" : ""}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprovar Pagamento</DialogTitle>
            <DialogDescription>
              Confirma a aprovação do pagamento{" "}
              <strong>{selectedPayment?.paymentCode}</strong>?
              {selectedPayment && (
                <span className="block mt-1">
                  Valor: {formatCurrencyValue(selectedPayment.amount, selectedPayment.currency)} via{" "}
                  {RAIL_LABELS[selectedPayment.paymentRail] || selectedPayment.paymentRail}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Notas do administrador (opcional)</Label>
              <Textarea
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                placeholder="Adicione observações sobre esta aprovação..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)} disabled={approving}>
              Cancelar
            </Button>
            <Button
              onClick={handleApprove}
              disabled={approving}
              className="bg-green-600 hover:bg-green-700"
            >
              {approving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Aprovar Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Pagamento</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja rejeitar o pagamento{" "}
              <strong>{selectedPayment?.paymentCode}</strong>? O saldo USDC bloqueado será
              desbloqueado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">
                Motivo da rejeição <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Descreva o motivo da rejeição..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)} disabled={rejecting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejecting || !rejectReason.trim()}
            >
              {rejecting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Rejeitar Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

